import type { ReactNode } from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

/* Clerk *publishable* keys are public by design — they ship in the browser
   bundle. `__CLERK_PUBLISHABLE_KEY__` is substituted at build time by
   vite.config.ts, which reads the single committed value from wrangler.toml so
   the bundle and the Worker cannot end up on different Clerk instances. The
   secret key is NEVER handled here — that stays server-side only.

   There is deliberately no runtime fallback: a hardcoded default silently pins
   the browser to whichever instance it names, which is how a stale development
   key survives a production cutover and turns into 401s with no visible cause. */
const PUBLISHABLE_KEY: string = __CLERK_PUBLISHABLE_KEY__;

/* Triton-flavored appearance so Clerk's hosted components fit the dashboard.
   Tokens mirror src/styles/tokens.css. */
const appearance = {
  variables: {
    colorPrimary: '#00abd8',
    colorText: '#1f2937',
    colorTextSecondary: '#5e6e82',
    colorBackground: '#ffffff',
    borderRadius: '12px',
    fontFamily: '"Work Sans", system-ui, sans-serif',
  },
};

/* ClerkProvider must live inside the Router so Clerk navigations use the SPA
   router instead of full page loads. */
export function ClerkProviderWithRouter({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      appearance={appearance}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      /* Route every auth flow through our in-app pages and back to the
         dashboard — without these, Clerk falls back to its hosted Account
         Portal (accounts.*.clerk.*) and invitees can get stranded there. */
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/sign-in"
    >
      {children}
    </ClerkProvider>
  );
}
